import {
  HomefieldIconChevronLeft,
  HomefieldIconChevronRight,
  HomefieldIconSignCancelWhite,
} from '@dsg-tech/homefield-react-icons';
import React, { useEffect, useState } from 'react';
import {
  CSVData,
  CTA,
  NavBarData,
  NikeConnected,
  PrimaryCategoryData,
  StoreIdentifierName,
} from 'types';
import { checkStores, setDataEmTag } from 'utility/utility';
import ErrorBoundaryWithLogger from 'components/common/ErrorBoundary/ErrorBoundary';
import { useAppSelector } from 'store/store';
import { getFeatureFlagByNameSelector } from 'store/selectors';
import { multiUseAnalyticsEvent } from 'utility/analytics-events';
import { KEY_CODES } from 'utility/constants';
import AccordionContainer from '../../Accordion/AccordionContainer';
import MobileNavBar from '../../NavBar/MobileNavBar';
import NavBoxes from '../../NavBoxes/NavBoxes';
import setIcon from '../../icon-selector';
import MyStore, { MyStoreFallback } from '../../MyStore/MyStore';
import SameDayDelivery from '../../SameDayDelivery/SameDayDelivery';
import './MobileNav.scss';

/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
interface MobileNavProps {
  mobileNavData: PrimaryCategoryData[] | undefined;
  navBarData: NavBarData[] | undefined;
  showMobileMenu: boolean;
  setShowMobileMenu: (value: boolean) => void;
  showMobileMenuClickHandler: () => void;
  storeIdentifierName: StoreIdentifierName;
  viewPortBreakpoint: string;
  nikeConnected: NikeConnected;
}
interface NavListItem {
  title: string;
  csvData: CSVData[];
  link: string;
  flyoutImage?: { title: string; link: string; linkTitle: string; imageUpload: string }[];
  cta?: CTA[];
  highlight: boolean;
}

export default function MobileNav({
  mobileNavData,
  navBarData,
  showMobileMenu,
  setShowMobileMenu,
  showMobileMenuClickHandler,
  storeIdentifierName,
  viewPortBreakpoint,
  nikeConnected,
}: MobileNavProps) {
  const [showSubMenu, setShowSubMenu] = useState(false);
  const [showMainNav, setShowMainNav] = useState(true);
  const [accordionCategories, setAccordionCategories] = useState<any>([]);
  const [mobileImageAndCtasData, setMobileImageAndCtasData] = useState<any>([]);
  const [shopAllCat, setShopAllCat] = useState<any>([]);
  const favoriteIconEnabled = useAppSelector(getFeatureFlagByNameSelector('addFavoritesEnabled'));
  const mobileFavorite = navBarData?.filter((link) => link.linkTitle === 'Favorites')[0];
  const filteredNavBarData = navBarData?.filter((links) => links.linkTitle !== 'Favorites');
  const displayFavoriteLink =
    viewPortBreakpoint === 'mobile' && mobileFavorite && favoriteIconEnabled;

  // prevents body from scrolling when mobile menu is open
  useEffect(() => {
    if (showMobileMenu) {
      document.body.classList.add('navigation-open');
    } else if (document.body.classList.contains('navigation-open')) {
      document.body.classList.remove('navigation-open');
    }
  }, [showMobileMenu]);

  const handleEscKeyDown = (e: KeyboardEvent) => {
    if (e.key === KEY_CODES.ESCAPE) {
      e.preventDefault();
      setShowMobileMenu(false);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleEscKeyDown);
    return () => {
      document.removeEventListener('keydown', handleEscKeyDown);
    };
  }, [showMobileMenu, showSubMenu]);

  const showMainMenu = () => {
    setShowSubMenu(false);
    setShowMainNav(true);
  };

  const backToMainNavClickHandlerKeyEvent = (e?: React.KeyboardEvent) => {
    if (e?.key === KEY_CODES.ENTER || e?.key === KEY_CODES.SPACE) {
      showMainMenu();
    }
  };

  function BackArrow() {
    return (
      <button
        className="mobile-nav-container__shop-all--icon-wrapper hmf-display-flex hmf-align-items-center hmf-justify-content-center hmf-py-m hmf-px-s"
        onClick={() => showMainMenu()}
        onKeyDown={(e) => backToMainNavClickHandlerKeyEvent(e)}
        tabIndex={0}
        type="button"
        aria-label="Navigate Back">
        <HomefieldIconChevronLeft height="32px" width="32px" />
      </button>
    );
  }

  function HeaderLink() {
    try {
      return (
        <div className="mobile-nav-container__shop-all hmf-display-flex hmf-align-items-center sticky">
          <BackArrow />
          <a
            className={`mobile-nav-container__shop-all--shop-all-title hmf-header-bold-s hmf-p-s hmf-text-decoration-none ${
              shopAllCat.title.toLowerCase() === 'clearance' ? 'clearance' : ''
            }`}
            data-em={setDataEmTag('TopNav', 'ShopAll', shopAllCat.title)}
            href={checkStores(shopAllCat.link)}>
            {shopAllCat.title}
            <div className="hmf-body-s hmf-mt-xxxs mobile-nav-container__shop-all--shop-all-text">
              Shop All
            </div>
          </a>
        </div>
      );
    } catch (error) {
      return null;
    }
  }

  const navCatClickHandler = (catData: any) => {
    if (catData.csvData && catData.csvData.length > 0) {
      setShowSubMenu(true);
      setShowMainNav(false);
      setMobileImageAndCtasData({
        flyoutImage: catData.flyoutImage,
        cta: catData.cta,
      });
      setAccordionCategories(catData.csvData[0].col);
      setShopAllCat(catData);
    } else {
      window.location.href = catData.link;
    }
  };

  function CloseButton() {
    return (
      <button
        className="close-button hmf-display-flex hmf-flex-column hmf-align-items-center hmf-justify-content-center hmf-p-xxs"
        type="button"
        aria-label="Close Menu"
        tabIndex={0}
        onClick={() => showMobileMenuClickHandler()}
        onKeyDown={(event) => {
          if (event.key === KEY_CODES.SPACE || event.key === KEY_CODES.ENTER) {
            showMobileMenuClickHandler();
          }
        }}>
        <HomefieldIconSignCancelWhite height="14px" width="14px" />
        <span className="hmf-body-bold-s hmf-mt-xxxs">Close</span>
      </button>
    );
  }

  function MobileSubMenu() {
    return (
      <div className="mobile-nav-container__sub-menu-container hmf-display-flex hmf-flex-column">
        <HeaderLink />
        <div className="sub-menu-body hmf-display-flex hmf-flex-column">
          <AccordionContainer
            accordionCatData={accordionCategories}
            mobileImageAndCtasData={mobileImageAndCtasData}
            dataEmCatTitle={shopAllCat.title}
            storeIdentifierName={storeIdentifierName}
          />
        </div>
      </div>
    );
  }

  const transformToNavListItem = (data: PrimaryCategoryData[]) => {
    return data.map((category) => {
      return {
        title: category.title,
        csvData: category.csvData,
        link: category.link,
        flyoutImage: category.flyoutImage,
        cta: category.cta,
        highlight: category.highlight ?? false,
      };
    });
  };

  const navListItemsData = mobileNavData ? transformToNavListItem(mobileNavData) : [];

  function navListItem(data: NavListItem) {
    try {
      return (
        <ErrorBoundaryWithLogger componentId="MobileNav-navListitem" key={`nav-${data.title}`}>
          <button
            type="button"
            className={`mobile-nav-container__nav--list-item hmf-display-flex hmf-text-align-left hmf-align-items-center hmf-justify-content-between hmf-text-decoration-none hmf-body-bold-l hmf-p-s hmf-px-md-m ${
              data.title.toLowerCase() === 'clearance' || data?.highlight ? 'clearance' : ''
            } `}
            onClick={() => navCatClickHandler(data)}
            onKeyDown={(event) => {
              if (event.key === KEY_CODES.ENTER) {
                navCatClickHandler(data);
              }
            }}
            data-em={setDataEmTag('TopNav', data.title)}
            tabIndex={0}>
            {data.title}
            <HomefieldIconChevronRight height="24px" width="24px" />
          </button>
        </ErrorBoundaryWithLogger>
      );
    } catch (error) {
      return null;
    }
  }

  function navListItems(data: NavListItem[]) {
    return (
      <div className="mobile-nav-container__nav hmf-display-flex hmf-flex-column">
        {data.map((category: any) => {
          return navListItem(category);
        })}
      </div>
    );
  }

  if (viewPortBreakpoint === 'desktop') return null;

  return (
    <div
      className={`mobile-nav-display ${showMobileMenu ? `hmf-display-block` : 'hmf-display-none'}`}>
      <nav className="mobile-nav-container">
        <div className="mobile-nav-backdrop" onClick={() => setShowMobileMenu(!showMobileMenu)} />
        <div
          className={`mobile-nav-container__nav-container ${
            showMobileMenu && showMainNav ? 'hmf-display-block' : 'hmf-display-none'
          }`}>
          {storeIdentifierName === 'dsg' && (
            <>
              <ErrorBoundaryWithLogger
                componentId="MobileNav-MyStore"
                fallback={<MyStoreFallback />}>
                <MyStore viewPortBreakpoint={viewPortBreakpoint} showInMobileMenu />
              </ErrorBoundaryWithLogger>
              <ErrorBoundaryWithLogger componentId="MobileNav-SameDayDelivery">
                <SameDayDelivery viewPortBreakpoint={viewPortBreakpoint} showInMobileMenu />
              </ErrorBoundaryWithLogger>
            </>
          )}
          {displayFavoriteLink && (
            <a
              className="mobile-nav-container__my-account hmf-p-s hmf-px-md-m hmf-display-flex hmf-display-l-none hmf-align-items-center hmf-justify-content-between hmf-text-decoration-none"
              tabIndex={0}
              href={checkStores(mobileFavorite.link)}
              onClick={() =>
                multiUseAnalyticsEvent({
                  event: 'Signed out athlete clicked Favorites from:',
                  value: 'Header',
                })
              }
              title={mobileFavorite.linkTitle}
              data-em={setDataEmTag('Header', mobileFavorite.linkTitle)}>
              <div className="hmf-display-flex">
                {setIcon(
                  mobileFavorite.mobileIcon ?? '',
                  storeIdentifierName,
                  'favorite-icon hmf-align-items-self-start',
                )}
                <div className="hmf-pl-xxs">
                  <div className="hmf-body-bold-l">{mobileFavorite.linkTitle}</div>
                  {mobileFavorite.description && (
                    <div className="hmf-body-s">{mobileFavorite.description}</div>
                  )}
                </div>
              </div>
              <HomefieldIconChevronRight height="24px" width="24px" />
            </a>
          )}

          {mobileNavData?.length && navListItems(navListItemsData)}

          <div className="mobile-nav-bar-display hmf-display-block hmf-display-lg-none">
            <div className="mobile-nav-container__spacer hmf-pl-xxs hmf-py-xs" />
            {storeIdentifierName === 'pl' || storeIdentifierName === 'mj' ? (
              <ErrorBoundaryWithLogger componentId="MobileNav-NavBoxes">
                <NavBoxes
                  navBarData={filteredNavBarData}
                  storeIdentifierName={storeIdentifierName}
                />
              </ErrorBoundaryWithLogger>
            ) : (
              <ErrorBoundaryWithLogger componentId="MobileNav-MobileNavBar">
                <MobileNavBar
                  navBarData={filteredNavBarData?.filter(
                    (links) => links.linkTitle !== 'Favorites',
                  )}
                  storeIdentifierName={storeIdentifierName}
                  nikeConnected={nikeConnected}
                />
              </ErrorBoundaryWithLogger>
            )}
          </div>
        </div>
        {showSubMenu && (
          <ErrorBoundaryWithLogger componentId="MobileNav-MobileSubMenu">
            <MobileSubMenu />
          </ErrorBoundaryWithLogger>
        )}
        <ErrorBoundaryWithLogger componentId="MobileNav-CloseButton">
          <CloseButton />
        </ErrorBoundaryWithLogger>
      </nav>
    </div>
  );
}
